import { useEffect, useState } from "react";
import PageHero from "@/components/PageHero";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Briefcase, Clock, Upload, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const BENEFITS = [
  ["Growth", "Structured career paths across five verticals."],
  ["Learning", "Industry certifications & mentorship programs."],
  ["Wellness", "Comprehensive health cover & family benefits."],
  ["Impact", "Work that improves rural India, tangibly."],
];

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [openJob, setOpenJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name:"", email:"", mobile:"", cover_letter:"" });
  const [resume, setResume] = useState(null);

  useEffect(() => {
    api.get("/careers/jobs").then(r => setJobs(r.data)).catch(() => setJobs([]));
  }, []);

  const apply = async (e) => {
    e.preventDefault();
    if (!openJob) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("mobile", form.mobile);
      fd.append("position", openJob.title);
      fd.append("cover_letter", form.cover_letter);
      if (resume) fd.append("resume", resume);
      const { data } = await api.post("/careers/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message || "Application submitted");
      setForm({ name:"", email:"", mobile:"", cover_letter:"" }); setResume(null); setOpenJob(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="careers-page">
      <PageHero
        eyebrow="Careers at Shramik"
        title={<>Build a career that <em className="not-italic font-serif italic text-shramik-green">builds rural India.</em></>}
        subtitle="Join a diversified enterprise where your work touches farmers, families and infrastructure — every single day."
      />

      {/* Benefits */}
      <section className="py-16">
        <div className="container-page grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {BENEFITS.map(([t,d], i) => (
            <motion.div key={t} initial={{opacity:0, y:20}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{duration:0.4, delay:i*0.05}}
              className="p-6 rounded-2xl bg-shramik-greenLight/60 border border-shramik-greenLight">
              <div className="font-display text-xl font-medium">{t}</div>
              <div className="text-sm text-slate-600 mt-2">{d}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Openings */}
      <section className="py-14" data-testid="job-openings">
        <div className="container-page">
          <div className="overline mb-4">Current Openings</div>
          <h2 className="font-display font-light text-4xl md:text-5xl mb-10 tracking-tight text-balance">Find your next role.</h2>

          <div className="grid gap-4">
            {jobs.map((j, i) => (
              <motion.div key={j.id} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.4, delay:i*0.03}}
                className="group flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 rounded-2xl bg-white border border-slate-100 hover:border-shramik-green transition-all hover:shadow-md"
                data-testid={`job-${j.id}`}>
                <div>
                  <div className="text-xs uppercase tracking-widest text-shramik-green font-semibold">{j.department}</div>
                  <div className="font-display text-xl md:text-2xl font-medium mt-1">{j.title}</div>
                  <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4"/>{j.location}</span>
                    <span className="flex items-center gap-1.5"><Briefcase className="w-4 h-4"/>{j.type}</span>
                    <span className="flex items-center gap-1.5"><Clock className="w-4 h-4"/>{j.experience}</span>
                  </div>
                </div>
                <button data-testid={`apply-btn-${j.id}`} onClick={() => setOpenJob(j)} className="btn-primary shrink-0">Apply <ArrowRight className="w-4 h-4"/></button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application modal */}
      {openJob && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={() => setOpenJob(null)} data-testid="apply-modal">
          <div className="bg-white rounded-3xl w-full max-w-2xl p-8 md:p-10 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-xs uppercase tracking-widest text-shramik-green font-semibold">Apply for</div>
            <h3 className="font-display text-3xl font-light mt-1">{openJob.title}</h3>
            <p className="text-slate-500 text-sm mt-1">{openJob.department} • {openJob.location}</p>

            <form onSubmit={apply} className="mt-8 grid gap-4">
              <input required data-testid="apply-name" placeholder="Full name" className="w-full border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-shramik-green" value={form.name} onChange={e => setForm({...form, name:e.target.value})}/>
              <div className="grid md:grid-cols-2 gap-4">
                <input required type="email" data-testid="apply-email" placeholder="Email" className="w-full border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-shramik-green" value={form.email} onChange={e => setForm({...form, email:e.target.value})}/>
                <input required data-testid="apply-mobile" placeholder="Mobile" className="w-full border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-shramik-green" value={form.mobile} onChange={e => setForm({...form, mobile:e.target.value})}/>
              </div>
              <textarea data-testid="apply-cover" placeholder="Cover letter (optional)" rows={4} className="w-full border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-shramik-green resize-none" value={form.cover_letter} onChange={e => setForm({...form, cover_letter:e.target.value})}/>

              <label className="w-full border-2 border-dashed border-slate-200 rounded-xl p-6 flex items-center gap-3 cursor-pointer hover:border-shramik-green transition-colors" data-testid="apply-resume-label">
                <Upload className="w-5 h-5 text-shramik-green"/>
                <div className="flex-1">
                  <div className="font-medium text-sm">{resume ? resume.name : "Upload resume (PDF/DOC/DOCX, ≤6MB)"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">We treat your data with care.</div>
                </div>
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" data-testid="apply-resume-input" onChange={e => setResume(e.target.files?.[0] || null)}/>
              </label>

              <div className="flex justify-end gap-3 mt-4">
                <button type="button" onClick={() => setOpenJob(null)} className="btn-ghost">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="apply-submit" className="btn-primary disabled:opacity-70">{submitting ? "Submitting..." : "Submit application"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
